/**
 * ContactCaptureSheet.tsx
 * Soft prompt for contact details. Skippable. Re-prompts after 10 mins.
 */
import { useState } from 'react'
import { X, Gift, Bell } from 'lucide-react'
import { captureContact, enterPrizeDraw, type EventWithEntry } from '@/lib/venueEntry'
import { cn } from './utils'

interface Props {
  participantId: string
  event?: EventWithEntry | null
  onDone: () => void
  onSkip: () => void
}

export function ContactCaptureSheet({ participantId, event, onDone, onSkip }: Props) {
  const [contact, setContact] = useState('')
  const [name, setName] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const isPrizeDraw = event?.prize_draw_enabled

  async function handleSubmit() {
    if (!contact.trim()) { setError('Enter your email or phone number'); return }
    setSaving(true)
    setError('')
    try {
      if (isPrizeDraw && event?.id) {
        await enterPrizeDraw(event.id, participantId, contact.trim())
      } else {
        await captureContact(participantId, contact.trim(), name.trim() || undefined)
      }
      onDone()
    } catch (err) {
      setError((err as Error).message ?? 'Could not save. Try again.')
    } finally {
      setSaving(false)
    }
  }

  const headline = isPrizeDraw
    ? (event?.prize_draw_label ?? 'Enter the prize draw')
    : 'Get your content'

  const subline = isPrizeDraw
    ? ('Enter your details for a chance to win' + (event?.sponsor_name ? ' from ' + event.sponsor_name : ''))
    : 'Add your details to release your shots to the marketplace and build your profile'

  const footerNote = isPrizeDraw
    ? 'No spam. Used only for draw notification and content delivery.'
    : 'Used to build your creator profile. No spam.'

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center px-4 pb-6">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onSkip} />

      <div className="relative w-full max-w-sm bg-room-surface border border-room-border rounded-3xl p-6 animate-slide-up space-y-5">
        <button
          onClick={onSkip}
          className="absolute top-4 right-4 w-7 h-7 rounded-full bg-neutral-800 flex items-center justify-center text-neutral-400 hover:text-white transition-colors"
        >
          <X size={14} />
        </button>

        <div className="flex items-start gap-3">
          <div className={cn(
            'w-10 h-10 rounded-2xl flex items-center justify-center shrink-0',
            isPrizeDraw ? 'bg-amber-500/20' : 'bg-room-accent/20'
          )}>
            {isPrizeDraw
              ? <Gift size={18} className="text-room-gold" />
              : <Bell size={18} className="text-room-accent" />
            }
          </div>
          <div>
            <p className="text-white font-bold text-base">{headline}</p>
            <p className="text-neutral-400 text-sm mt-0.5">{subline}</p>
          </div>
        </div>

        <div className="space-y-3">
          <input
            autoFocus
            value={contact}
            onChange={e => { setContact(e.target.value); setError('') }}
            onKeyDown={e => { if (e.key === 'Enter') handleSubmit() }}
            placeholder="Email or phone number"
            className={cn(
              'w-full bg-black/40 border rounded-xl px-4 py-3 text-white placeholder-neutral-600 text-sm',
              'focus:outline-none focus:ring-2 focus:ring-room-accent focus:border-transparent transition-all',
              error ? 'border-room-error' : 'border-room-border'
            )}
          />
          {isPrizeDraw && (
            <input
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Your name (optional)"
              className="w-full bg-black/40 border border-room-border rounded-xl px-4 py-3 text-white placeholder-neutral-600 text-sm focus:outline-none focus:ring-2 focus:ring-room-accent focus:border-transparent transition-all"
            />
          )}
          {error && <p className="text-room-error text-xs">{error}</p>}
        </div>

        <div className="flex gap-3">
          <button
            onClick={onSkip}
            className="flex-1 py-3 rounded-xl bg-neutral-800 hover:bg-neutral-700 text-neutral-400 hover:text-white text-sm font-medium transition-colors"
          >
            Maybe later
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className={cn(
              'flex-1 py-3 rounded-xl text-white text-sm font-bold transition-colors',
              isPrizeDraw ? 'bg-amber-500 hover:bg-amber-400' : 'bg-room-accent hover:bg-fuchsia-600'
            )}
          >
            {saving ? '...' : isPrizeDraw ? 'Enter draw' : 'Release my shots'}
          </button>
        </div>

        <p className="text-center text-xs text-neutral-600">{footerNote}</p>
      </div>
    </div>
  )
}
